import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import axios from "axios";

import { fetchStatus } from "./preferenceSlice";
import { getUrl } from "../../helpers/url";

const initialState = {
  searchInput: "",
  searchResultsArray: [],
  selectPreferenceStripe: undefined,
  currentlyEditing: undefined,
  isEditMapping: false,
  metadataPreference: {
    customer: "",
    invoice: "",
  },
  isMappingsSaved: {
    customer: true,
    invoice: true,
    product: true,
  },
  stripe: {
    loading: false,
    stripeDropdownFields: {},
    error: "",
    currentRequestId: undefined,
  },
  msbc: {
    loading: false,
    msbcDropdownFields: {},
    error: "",
    currentRequestId: undefined,
  },
  mappings: {
    loading: false,
    mappings: {
      customer: [],
      invoice: [],
      product: [],
    },
    error: "",
    currentRequestId: undefined,
  },
  // pagination
  itemOffset: 0,
  slicedMappings: [],
  pageCount: 0,
  selectedIndex: 0,
};

/**
 * This function is used for fetching Stripe fields
 */
export const fetchStripeFields = createAsyncThunk(
  "mapping/fetchStripeFields",
  async (account) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_GET_STRIPE_FIELDS")}?account=${account}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
      throw error;
    }
  }
);

/**
 * This function is used for fetching msbc fields
 */
export const fetchMSBCFields = createAsyncThunk(
  "mapping/fetchMSBCFields",
  async (account) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_GET_MSBC_FIELDS")}?account=${account}`
      );
      return response;
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
      throw error;
    }
  }
);

export const mappingSlice = createSlice({
  name: "mapping",
  initialState,
  reducers: {
    /**
     * @description This function is used to update the page count
     */
    updatePageCount(state, { payload }) {
      state.pageCount = payload.count;
    },
    /**
     * @description This function is used to update itemOffset
     */
    updateItemOffset(state, { payload }) {
      state.itemOffset = payload.itemOffset;
    },
    /**
     * @description This function is used to update the selected index
     */
    updateSelectedIndex(state, { payload }) {
      state.selectedIndex = payload.selectedIndex;
    },
    /**
     * @description This function is used for updating custom mappings
     */
    updateSlicedMappings(state, { payload }) {
      state.slicedMappings = payload.mappings;
    },
    /**
     * This reducer updates the state of mappings
     */
    checkUnmappedMSBCFields(state, { payload }) {
      const newSet = new Set(
        state.mappings.mappings[payload.mappingsFor].map(
          (mappedItem) => mappedItem.Msbc_FieldName_CRF
        )
      );

      state.msbc.msbcDropdownFields[payload.mappingsFor].forEach(
        (msbcProperty) => {
          if (!newSet.has(msbcProperty.CRF)) {
            const newMapping = {
              Stripe_FieldName_CRF: undefined,
              Msbc_FieldName_CRF: msbcProperty.CRF,
              Stripe_FieldName_HRF: undefined,
              Msbc_FieldName_HRF: msbcProperty.HRF,
              Stripe_FieldType: undefined,
              Msbc_FieldType: msbcProperty.type,
              Stripe_Field_Description: undefined,
              Msbc_Field_Description: msbcProperty.description,
              id: `${msbcProperty.CRF}-${payload.mappingsFor}`,
            };
            if (msbcProperty.required === true) {
              newMapping.Msbc_Field_Required = true;
            }
            state.mappings.mappings[payload.mappingsFor].push(newMapping);
          }
        }
      );
    },
    /**
     * Reducer updates the state for selected options in dropdown
     */
    setStripePreference(state, { payload }) {
      state.selectPreferenceStripe = payload.selectedOption;
    },
    /**
     * Reducer updates the state while editing mappings
     */
    onEditMapping(state, { payload }) {
      state.currentlyEditing = payload.mappedItem.id;
      state.isEditMapping = true;
      if (
        payload.mappedItem?.Stripe_FieldName_HRF &&
        payload.mappedItem?.Stripe_FieldName_HRF === "MetaData"
      ) {
        state.metadataPreference[payload.mappingsFor] =
          payload.mappedItem.Stripe_FieldName_CRF;
      }
    },
    /**
     * Returns mappings based on search input
     */
    onSearchMappings(state, { payload }) {
      state.searchInput = payload.enteredInput;
      state.searchResultsArray = [];
      if (state.searchInput !== "") {
        state.mappings.mappings[payload.mappingsFor].forEach((mappedItem) => {
          if (
            (mappedItem.Stripe_FieldName_HRF !== undefined &&
              mappedItem.Stripe_FieldName_HRF.toLowerCase().includes(
                payload.enteredInput.toLowerCase()
              )) ||
            mappedItem.Msbc_FieldName_HRF.toLowerCase().includes(
              payload.enteredInput.toLowerCase()
            )
          )
            state.searchResultsArray.push(mappedItem.id);
        });
      }
    },
    /**
     * Reducers resets the state of preferences required for mapping when user clicks on cancel icon
     */
    onCancelMapping(state, { payload }) {
      state.currentlyEditing = undefined;
      state.isEditMapping = false;
      state.selectPreferenceStripe = undefined;
      state.metadataPreference = {
        customer: "",
        invoice: "",
      };
    },
    /**
     * Reducer updates the state of mappings when user clicks on delete icon
     */
    onDeleteExistingMapping(state, { payload }) {
      const index = state.mappings.mappings[payload.mappingsFor].findIndex(
        (mappedItem) => mappedItem.id === payload.id
      );

      state.mappings.mappings[payload.mappingsFor][index] = {
        ...state.mappings.mappings[payload.mappingsFor][index],
        Stripe_FieldName_CRF: undefined,
        Stripe_FieldName_HRF: undefined,
        Stripe_FieldType: undefined,
        Stripe_Field_Description: undefined,
      };

      state.isMappingsSaved[payload.mappingsFor] = false;
      state.metadataPreference = {
        customer: "",
        invoice: "",
      };
    },
    /**
     * Reducer updates the state of mappings when user clicks on check icon after selecting mappings
     */
    onSaveEditedMappings(state, { payload }) {
      if (state.selectPreferenceStripe !== "") {
        const index = state.mappings.mappings[payload.mappingsFor].findIndex(
          (mappedItem) => mappedItem.id === payload.id
        );

        state.mappings.mappings[payload.mappingsFor][index] = {
          ...state.mappings.mappings[payload.mappingsFor][index],
          Stripe_FieldName_CRF:
            state.selectPreferenceStripe?.CRF === "key" ||
            state.mappings.mappings[payload.mappingsFor][index]
              .Stripe_FieldName_HRF === "MetaData"
              ? !state.metadataPreference[payload.mappingsFor]
                ? state.mappings.mappings[payload.mappingsFor][index]
                    .Stripe_FieldName_CRF
                : state.metadataPreference[payload.mappingsFor]
              : state.selectPreferenceStripe
              ? state.selectPreferenceStripe.CRF
              : state.mappings.mappings[payload.mappingsFor][index]
                  .Stripe_FieldName_CRF,
          Stripe_FieldName_HRF: state.selectPreferenceStripe
            ? state.selectPreferenceStripe.HRF
            : state.mappings.mappings[payload.mappingsFor][index]
                .Stripe_FieldName_HRF,
          Stripe_FieldType: state.selectPreferenceStripe
            ? state.selectPreferenceStripe.type
            : state.mappings.mappings[payload.mappingsFor][index]
                .Stripe_FieldType,
          Stripe_Field_Description: state.selectPreferenceStripe
            ? state.selectPreferenceStripe.description
            : state.mappings.mappings[payload.mappingsFor][index]
                .Stripe_Field_Description,
        };
      }

      state.selectPreferenceStripe = undefined;
      state.isEditMapping = false;
      state.currentlyEditing = undefined;
      state.isMappingsSaved[payload.mappingsFor] = false;
      state.metadataPreference = {
        customer: "",
        invoice: "",
      };
    },
    /**
     * Reducer updates the states of preferences when users switches the tabs
     */
    clearDefaultValues(state) {
      state.currentlyEditing = undefined;
      state.isEditMapping = false;
      state.selectPreferenceStripe = undefined;
      state.searchInput = "";
      state.searchResultsArray = [];
      state.selectedIndex = 0;
      state.pageCount = 0;
      state.itemOffset = 0;
      state.metadataPreference = {
        customer: "",
        invoice: "",
      };
    },
    /**
     * Reducer updates the state of mappings saved
     */
    checkIsMappingsSaved(state, { payload }) {
      state.isMappingsSaved[payload.mappingsFor] = true;
    },
    /**
     * Reducer updates the state of metadata of mappings fields
     */
    onChangeMappingsMetadata(state, { payload }) {
      state.metadataPreference[payload.mappingsFor] = payload.enteredInput;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStripeFields.pending, (state, actions) => {
      if (!state.stripe.loading) {
        state.stripe.loading = true;
        state.stripe.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchStripeFields.fulfilled, (state, actions) => {
      if (state.stripe.loading) {
        state.stripe.loading = false;
        state.stripe.stripeDropdownFields = actions.payload;
        state.stripe.error = "";
        state.stripe.currentRequestId = undefined;

        if (state.stripe.stripeDropdownFields.customer) {
          state.stripe.stripeDropdownFields.customer.push({
            HRF: "Do Not add it anywhere",
            CRF: "doNotAdd",
            type: "string",
            description: "",
          });
        }
      }
    });
    builder.addCase(fetchStripeFields.rejected, (state, actions) => {
      if (state.stripe.loading) {
        state.stripe.loading = false;
        state.stripe.stripeDropdownFields = {};
        state.stripe.error = actions.error.message ?? "";
        state.stripe.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchMSBCFields.pending, (state, actions) => {
      if (!state.msbc.loading) {
        state.msbc.loading = true;
        state.msbc.currentRequestId = actions.meta.requestId;
      }
    });
    builder.addCase(fetchMSBCFields.fulfilled, (state, actions) => {
      if (
        state.msbc.loading &&
        state.msbc.currentRequestId === actions.meta.requestId
      ) {
        state.msbc.loading = false;
        state.msbc.msbcDropdownFields = actions.payload;
        state.msbc.error = "";
        state.stripe.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchMSBCFields.rejected, (state, actions) => {
      if (
        state.msbc.loading &&
        state.msbc.currentRequestId === actions.meta.requestId
      ) {
        state.msbc.loading = false;
        state.msbc.msbcDropdownFields = {};
        state.msbc.error = actions.error.message ?? "";
        state.stripe.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      const { mapping } = actions.payload;
      if (mapping && Object.keys(mapping).length > 0) {
        const { customer, invoice, product } = mapping;
        state.mappings.mappings.customer = customer ?? [];
        state.mappings.mappings.invoice = invoice ?? [];
        state.mappings.mappings.product = product ?? [];

        const dummyObject = { customer: [], invoice: [], product: [] };

        Object.keys(state.mappings.mappings).forEach((category) => {
          state.mappings.mappings[category].forEach((mappedItem) => {
            dummyObject[category].push({
              ...mappedItem,
              id: `${mappedItem.Msbc_FieldName_CRF}-${category}`,
            });
          });
        });
        state.mappings.mappings = dummyObject;
      }
    });
  },
});

export const {
  checkUnmappedMSBCFields,
  setStripePreference,
  onEditMapping,
  onSearchMappings,
  onCancelMapping,
  onDeleteExistingMapping,
  onSaveEditedMappings,
  clearDefaultValues,
  checkIsMappingsSaved,
  updateItemOffset,
  updatePageCount,
  updateSelectedIndex,
  updateSlicedMappings,
  onChangeMappingsMetadata,
} = mappingSlice.actions;

export default mappingSlice.reducer;
